import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import {
  PWSSessionBackend,
  RecurlyBillingInfo,
  ZypeSubscription,
  ZypeSubscriptionPlan,
  COAST_MESSAGES,
} from "@inferno/renderer-shared-core";
import { Button, ButtonKind } from "../../../../../../core/ui";
import Heading from "../../../../../../core/ui/Heading.component";
import { axiosErrorReason } from "../../../../lib/coastCommon";
import { getPlanIntervalInMonths } from "../../../../lib/coastUser";
import { CoastAlert, CoastAlertSuccess } from "../../common/coast-alert/CoastAlert.model";
import { CoastAlertDisplayError } from "../../common/coast-alert/CoastAlertDisplay.component";
import { CoastSubscriptionDisclaimer } from "../../common/CoastSubscriptionDisclaimer.component";
import { UsersButton } from "../../common/UsersButton.components";
import { CoastUserBillingInfoWrapper } from "../billing/CoastUserBillingInfoWrapper.component";
import { CoastUserBillingInfoDisplay } from "../CoastUserSettingsBillingInfoDisplay";

interface CoastUserChangePlanConfirmationProps {
  setBillingInfo: (billingInfo: RecurlyBillingInfo) => void;
  goToMainView: (v?: CoastAlert[]) => void;
  billingInfo?: RecurlyBillingInfo | null;
  billingInfoError: CoastAlert | null;
  subscription: ZypeSubscription;
  nextPlan: ZypeSubscriptionPlan;
  session: PWSSessionBackend;
  goBack: () => void;
}

function isUpgrade(currentSub: ZypeSubscription, nextPlan: ZypeSubscriptionPlan) {
  return getPlanIntervalInMonths(currentSub) < getPlanIntervalInMonths(nextPlan);
}

function getPlanName(plan: ZypeSubscriptionPlan) {
  return `${plan.interval_count} ${plan.interval}`;
}

// This component supports both up and downgrade
// Currently we'll only be allowing upgrades
export function CoastUserChangePlanConfirmation({
  goToMainView,
  subscription,
  nextPlan,
  session,
  goBack,
  ...props
}: CoastUserChangePlanConfirmationProps) {
  const [editBillingInfo, setEditBillingInfo] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [formError, setFormError] = useState("");

  const curSubscriptionEndDate = useMemo(
    () => format(new Date(subscription.current_period_end_at), "MMMM dd, yyyy"),
    [subscription],
  );
  const isUpgrading = useMemo(() => isUpgrade(subscription, nextPlan), [subscription, nextPlan]);
  const nextPlanName = useMemo(() => getPlanName(nextPlan), [nextPlan]);

  const handlePlanChange = useCallback(
    async (event: React.SyntheticEvent) => {
      event.preventDefault();

      if (!session.state.accessToken) {
        return setFormError(COAST_MESSAGES.MISSING_ACCESS_TOKEN);
      }

      setIsUpdatingPlan(true);

      try {
        await session.memberService.upDownGradeSubscription(session.state.accessToken, subscription._id, nextPlan._id);
        // we can't update subscription display/cache right away here since up/downgrade isn't supported by Zype
        // so the only way to get an update is
        // for Zype to process Recurly webhook
        // for a user to refresh the page after that
        goToMainView([new CoastAlertSuccess(COAST_MESSAGES.SUBSCRIPTION_UPDATE_SUCCESS)]);
      } catch (e) {
        setFormError(axiosErrorReason(e) || COAST_MESSAGES.SUBSCRIPTION_UPDATE_ERROR);
        setIsUpdatingPlan(false);
      }
    },
    [goToMainView, nextPlan._id, session.memberService, session.state.accessToken, subscription._id],
  );

  if (editBillingInfo) {
    return (
      <CoastUserBillingInfoWrapper
        goBack={() => setEditBillingInfo(false)}
        fetchingError={props.billingInfoError}
        billingInfo={props.billingInfo}
        onUpdate={props.setBillingInfo}
        session={session}
      />
    );
  }

  return (
    <section className="component-account-subscriptions-edit">
      <Button className="button-back-to-account" kind={ButtonKind.CUSTOMIZED} click={() => goBack()}>
        Back
      </Button>

      <section className="account-sub-section">
        <CoastAlertDisplayError isDisplayed={!!formError} message={formError} />

        <Heading className="capitalized" level={2}>
          Change to {nextPlanName} Plan
        </Heading>

        <CoastUserBillingInfoDisplay
          onEditClick={() => setEditBillingInfo(true)}
          fetchingError={props.billingInfoError}
          billingInfo={props.billingInfo}
          isSubMenu={true}
        />

        <div className="spaced-out-block">
          <hr className="divider" />
        </div>

        <div className="spaced-out-block">
          <Heading level={3}>What Happens Next:</Heading>
          <ul>
            <li>
              Your new <span className="capitalized">{nextPlanName}</span> plan
              {isUpgrading
                ? " starts today."
                : ` will take effect at the next billing cycle on ${curSubscriptionEndDate}.`}
            </li>
            <li>
              You will be charged ${nextPlan.amount} (auto renews every {nextPlanName}
              {nextPlan.interval_count > 1 ? "s" : ""}).
            </li>
            {isUpgrading ? <li>We will apply the remainder of your current bill to your next plan.</li> : null}
          </ul>
        </div>

        <hr className="divider" />

        <CoastSubscriptionDisclaimer isGift={false} plan={nextPlan} />

        <UsersButton
          disabled={isUpdatingPlan}
          isProcessing={isUpdatingPlan}
          click={e => !isUpdatingPlan && handlePlanChange(e)}
          label="Confirm Change"
          type="submit"
        >
          Confirm Change
        </UsersButton>
      </section>
    </section>
  );
}
